import { useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import c from 'classnames';

import useSubscriptions from 'services/general/subscriptionsList';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { capitalize } from 'helpers';
import { Subscription, SubscriptionStatus } from 'types/Subscription';

import { TextLink } from '@repo/ui';
import { Card, ContainerSizes, PageContainer, Typography } from 'components';
import testPackage from 'assets/images/subscription.jpg';

import styles from './Subscriptions.module.css';

export const StatusPill = ({ status }: { status: SubscriptionStatus }) => {
  return (
    <div className={c(styles.pill, styles[status])}>{capitalize(status)}</div>
  );
};

const SubscriptionCard = ({ subscription }: { subscription: Subscription }) => {
  const { trackEvent } = useAnalytics();
  const numKits =
    (subscription.shopify_product?.num_kits_gut ?? 0) +
    (subscription.shopify_product?.num_kits_vaginal ?? 0) +
    (subscription.shopify_product?.num_kits_stool_chemistry ?? 0);
  const numCredits = subscription.shopify_product?.consult_credits ?? 0;
  const isCancelled = subscription.status === SubscriptionStatus.CANCELLED;

  const onCancelClick = () => {
    trackEvent('Cancel Subscription Clicked', {
      subscription_name: subscription.shopify_product_name,
      subscription_price: parseFloat(subscription.price),
      renews_date: subscription.date_next_charge_scheduled,
    });
  };

  return (
    <div className={styles.subscriptionCard}>
      <div
        className={c(
          styles.subscriptionImage,
          subscription.status !== SubscriptionStatus.ACTIVE &&
            styles.deactivated,
        )}
      >
        <img src={testPackage} alt={`Test package`} />
      </div>
      <div className={styles.subscriptionContent}>
        <div className={styles.subscriptionContentInfo}>
          <Typography
            variant='heading-m'
            className={
              subscription.status !== SubscriptionStatus.ACTIVE
                ? 'dark-grey'
                : ''
            }
          >
            {subscription.shopify_product_name}
          </Typography>
          <Typography variant='label' className='dark-grey'>
            {`${numKits} test kits${numCredits ? `, ${numCredits} coaching session` : ''}`}
          </Typography>
          {!isCancelled && (
            <Typography variant='label' className='dark-grey'>
              Renews{' '}
              {format(
                new Date(subscription.date_next_charge_scheduled),
                'MM/dd/yy',
              )}
            </Typography>
          )}
          <div className='flex align-items-center gap-1'>
            {(!!subscription?.next_charge?.total_price ||
              !!subscription.price) && (
              <Typography variant='label' className='dark-grey'>
                {'$' +
                  (subscription?.next_charge?.total_price
                    ? subscription.next_charge.total_price
                    : subscription.price)}
              </Typography>
            )}
            {!!subscription?.next_charge?.total_price &&
              subscription?.next_charge?.total_price != subscription.price && (
                <Typography variant='label' className='dark-grey line-through'>
                  {'$' + subscription.price}
                </Typography>
              )}
          </div>
        </div>
        <div className='flex align-items-center gap-4 flex-1'>
          <StatusPill status={subscription.status} />
          {!isCancelled && (
            <TextLink
              to={`/account/subscriptions/${subscription.id}/cancellation`}
              label='Cancel'
              iconNameLeft='trash'
              color='red'
              className='ml-auto'
              onClick={onCancelClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const Subscriptions = () => {
  const { trackEvent } = useAnalytics();
  const { data: subscriptionsList } = useSubscriptions();
  const hasTrackedView = useRef(false);

  const activeSubscriptions = useMemo(
    () =>
      subscriptionsList?.filter(
        subscription => subscription.status === 'active',
      ) ?? [],
    [subscriptionsList],
  );
  const inactiveSubscriptions = useMemo(
    () =>
      subscriptionsList?.filter(
        subscription => subscription.status !== 'active',
      ) ?? [],
    [subscriptionsList],
  );

  useEffect(() => {
    if (!subscriptionsList?.length || hasTrackedView.current) return;
    hasTrackedView.current = true;
    trackEvent('Subscriptions Viewed', {
      active_count: activeSubscriptions.length,
      inactive_count: inactiveSubscriptions.length,
    });
  }, [
    subscriptionsList,
    activeSubscriptions,
    inactiveSubscriptions,
    trackEvent,
  ]);

  if (!subscriptionsList?.length) return <></>;
  return (
    <PageContainer size={ContainerSizes.MD} className={styles.pageContainer}>
      <div className='mt-3'>
        <TextLink
          to='/account'
          label='Back to Account'
          iconNameLeft='chevronBack'
          size='body-s'
        />
      </div>
      <Card variant='wrapper' className={styles.cardPadding}>
        <Typography variant='heading-l'>Your Active Subscriptions</Typography>
        {!activeSubscriptions.length && (
          <Typography variant='body-s'>No active subscriptions</Typography>
        )}
        {activeSubscriptions.map(subscription => (
          <SubscriptionCard subscription={subscription} key={subscription.id} />
        ))}
      </Card>
      <Card variant='wrapper' className={styles.cardPadding}>
        <Typography variant='heading-l'>Your inactive Subscriptions</Typography>
        {!inactiveSubscriptions.length && (
          <Typography variant='body-s'>No inactive subscriptions</Typography>
        )}
        {inactiveSubscriptions.map(subscription => (
          <SubscriptionCard subscription={subscription} key={subscription.id} />
        ))}
      </Card>
      <div className={styles.contact}>
        <Typography variant='heading-m'>Contact us</Typography>
        <Typography variant='body-s'>
          For help with subscription management or questions, please email
          hello@tinyhealth.com
        </Typography>
      </div>
    </PageContainer>
  );
};
